import { twJoin } from "tailwind-merge";

import type { MessageV2Dto } from "@/api/types";
import { Lock01Icon } from "@/components/Icons/Lock01Icon";
import { MessageSquare02Icon } from "@/components/Icons/MessageSquare02Icon";
import { Button, buttonVariants } from "@/design-system/Button";
import { Inline } from "@/design-system/Inline";
import { getReactionImage, getReactionLabel, getTotalReactionAmount } from "@/helpers/reactions";
import { twResolve } from "@/helpers/tw-resolve";
import { useTranslation } from "@/translations";

interface CommunityMessageReactionCountButtonProps {
  size: "sm" | "md";
  counts: MessageV2Dto["totalReactionCountByType"];
  isActive?: boolean;
  isDisabled?: boolean;
  onClick?: () => void;
}

export function CommunityMessageReactCountButton({
  size,
  counts,
  isActive,
  isDisabled,
  onClick,
}: CommunityMessageReactionCountButtonProps): React.ReactNode {
  const { t } = useTranslation();

  // Onlly show top 3 reactions by occurrence
  const topReactions = Object.entries(counts)
    .filter(([, count]) => count > 0)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 3)
    .map((entry) => entry[0]);

  return (
    <CommunityPostActivityButtonWrapper
      {...{
        isActive,
      }}
    >
      <button
        type="button"
        onClick={onClick}
        disabled={isDisabled}
        className={twResolve(
          buttonVariants({ variant: "subtle", size, type: "button" }),
          "rounded-md bg-background-secondary px-space-sm text-text-primary hover:bg-background-brand-light focus-visible:bg-background-brand-light",
          isActive && "bg-background-brand-light",
          !isActive && "font-normal",
        )}
        data-testid={size === "md" ? "reaction-count-btn" : "comment-reaction-count-btn"}
      >
        <Inline align="center" wrap={false}>
          {topReactions.map((reactionType) => (
            <img
              key={reactionType}
              src={getReactionImage(reactionType)}
              alt={getReactionLabel(reactionType, t)}
              className={twJoin(size === "md" && "h-icon-control", size === "sm" && "h-icon-inline")}
            />
          ))}
        </Inline>
        {getTotalReactionAmount(counts)}
      </button>
    </CommunityPostActivityButtonWrapper>
  );
}
interface CommunityPostCommentButtonProps {
  count: number;
  isActive: boolean;
  isDisabled?: boolean;
  onClick: () => void;
}
export function CommunityPostCommentButton({
  count,
  isActive,
  isDisabled,
  onClick,
}: CommunityPostCommentButtonProps): React.ReactNode {
  const { t } = useTranslation();

  return (
    <CommunityPostActivityButtonWrapper
      {...{
        isActive,
      }}
    >
      <Button
        variant="subtle"
        disabled={isDisabled}
        data-testid="comment-btn"
        icon={isDisabled ? <Lock01Icon /> : <MessageSquare02Icon />}
        isCircular
        emphasis={isActive}
        {...{
          onClick,
        }}
      >
        {t("component.community-post.comments.total", {
          count: count,
        })}
      </Button>
    </CommunityPostActivityButtonWrapper>
  );
}
interface CommunityPostActivityButtonWrapperProps {
  children: React.ReactNode;
  isActive?: boolean;
}
function CommunityPostActivityButtonWrapper({ children, isActive }: CommunityPostActivityButtonWrapperProps) {
  return (
    <div className="relative">
      {children}
      {isActive && (
        <span
          aria-hidden="true"
          className="absolute -bottom-1 left-0 h-px w-full translate-y-full rounded-t-full bg-background-brand"
        />
      )}
    </div>
  );
}
